<script setup lang="ts">
//#region import
import { ref } from 'vue';
import type { EmitsToListeners } from '@/types/core';
//#endregion

export interface Props {
  config?: any
  /** Текст заголовка */
  title?: string,
  /** Иконка в теле сообщения */
  bodyIcon?: string | null,
  /** Цвет иконки в теле сообщения */
  bodyIconColor?: string,
  /** Текст (с поддержкой html-тэгов) который оторажается в теле сообщения */
  message?: string,
  /** Тип офомления кнопки отмены */
  cancelType?: string,
  /** Иконка кнопки отмены */
  cancelIcon?: string,
  /** Надпись на кнопке отмены */
  cancelTitle?: string,
  /** Тип офомления кнопки подтверждения */
  confirmType?: string,
  /** Иконка кнопки подтверждения */
  confirmIcon?: string,
  /** Надпись на кнопке подтверждения */
  confirmTitle?: string,
  /** Тип офомления альтернативной (центральной) кнопки */
  alternativeType?: string,
  /** Иконка альтернативной (центральной) кнопки */
  alternativeIcon?: string,
  /** Надпись на альтернативной (центральной) кнопке */
  alternativeTitle?: string,
  /** Добавить альтернативную (центральную) кнопку в попап */
  isAlternative?: boolean,
  /** Спрятать кнопку отмены */
  hideCancelButton?: boolean,
}

export interface  Emits {
  /** Попап закрывается */
  'close': [],
  /** Кнопка подтверждения нажата */
  'success': [],
  /** Альтернативная (центральная) кнопка нажата */
  'alternative': []
}

export interface ConfirmParams {
  data?: Props;
  listeners?: EmitsToListeners<Emits>
}

//#region props
const props = withDefaults(defineProps<Props>(), {
  config: {},
  title: '',
  bodyIcon: null,
  bodyIconColor: 'var(--text-positive)',
  message: '',
  cancelType: 'negative',
  cancelIcon: 'times',
  cancelTitle: '',
  alternativeType: 'negative',
  alternativeIcon: 'times',
  alternativeTitle: '',
  confirmType: 'positive',
  confirmIcon: 'check',
  confirmTitle: '',
  isAlternative: false,
  hideCancelButton: false,
});
//#endregion

//#region emits
const emit = defineEmits<Emits>();
//#endregion

//#region data
const isVisible = ref(false);
//#endregion

//#region methods
const open = () => {
  isVisible.value = true;
};

const handleClose = () => {
  isVisible.value = false;
  emit('close');
};

const handleAlternative = async () => {
  emit('alternative');
};

const handleConfirm = async () => {
  emit('success');
};

//#endregion
defineExpose({
  open,
  handleClose,
});
</script>

<template>
  <Popup
    v-model:is-visible="isVisible"
    class="confirm-popup"
    :title="title"
    :width="480"
    @close="handleClose"
  >
    <template v-slot:body>
      <div
        class="confirm-popup__body"
      >
        <slot>
          <UiIcon
            v-if="bodyIcon"
            :size="24"
            :color="bodyIconColor"
            :name="bodyIcon"
            class="t:mr-12"
          />
          <div
            class="confirm-popup__message"
            v-html="message"
          />
        </slot>
      </div>
    </template>
    <template v-slot:footer>
      <div
        class="t:justify-end t:flex t:space-x-10"
      >
        <UiButton
          v-if="!hideCancelButton"
          :type="cancelType"
          :icon="cancelIcon"
          size="big"
          @click="handleClose"
        >
          {{ cancelTitle || $t('crm.buttons.cancel') }}
        </UiButton>
        <UiButton
          v-if="isAlternative"
          :type="alternativeType"
          :icon="alternativeIcon"
          size="big"
          @click="handleAlternative"
        >
          {{ alternativeTitle || $t('affiliates.cardView.toolbar.btns.dontSave') }}
        </UiButton>
        <UiButton
          :type="confirmType"
          :icon="confirmIcon"
          size="big"
          filled
          @click="handleConfirm"
        >
          {{ confirmTitle || $t('crm.buttons.save') }}
        </UiButton>
      </div>
    </template>
  </Popup>
</template>

<style lang="scss">
.confirm-popup {
  &__body{
    display: flex;
  }
  &__message {
    min-width: 0;
    color: var(--text-body);
    font-size: 14px;
    line-height: 24px;
    b {
      font-weight: 500;
    }
  }
  &__ellipsis {
    display: inline-block;
    max-width: calc(100% - 1em);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    vertical-align: bottom;
  }
}
</style>
